import { ApiService } from "../../../../config";
import type { Profile } from "./types";
import {
  buildServiceAreaSuggestions,
  type ServiceAreaSuggestion,
} from "./service-area-suggestions";

type RemoteOptions = {
  radiusKm?: number;
  query?: string;
  limit?: number;
};

const normalize = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const buildAddress = (profile: Partial<Profile>): string | undefined => {
  const parts = [
    profile.address,
    profile.city,
    profile.state,
    profile.country || "Brazil",
  ].filter((value): value is string => Boolean(value && value.trim()));
  return parts.length ? parts.join(", ") : undefined;
};

export async function fetchServiceAreaSuggestions(
  profileId: string,
  profile: Partial<Profile>,
  options: RemoteOptions = {},
): Promise<ServiceAreaSuggestion[]> {
  const radiusKm = options.radiusKm ?? 15;
  const limit = options.limit ?? 20;
  const localFallback = () =>
    buildServiceAreaSuggestions(profile as Profile, {
      radiusKm,
      query: options.query,
      limit,
    });

  try {
    const params: Record<string, string | number> = { radiusKm, limit };
    const lat = profile.latitude;
    const lng = profile.longitude;
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      params.lat = lat as number;
      params.lng = lng as number;
    } else {
      const address = buildAddress(profile);
      if (address) params.address = address;
    }

    const response = await ApiService.get(
      `/business-profiles/${profileId}/coverage-suggestions`,
      { params },
    );
    const data = (response?.data?.data ?? []) as Array<{
      label: string;
      distanceKm: number;
    }>;
    const existing = new Set(
      (profile.serviceAreas || []).map((area) => normalize(area)),
    );
    const query = options.query ? normalize(options.query) : "";
    const mapped = data
      .filter((item) => item?.label)
      .filter((item) => !existing.has(normalize(item.label)))
      .filter((item) => (query ? normalize(item.label).includes(query) : true))
      .map((item) => ({
        distanceKm: item.distanceKm,
        label: item.label,
        source: "curated" as const,
      }))
      .sort((a, b) => a.distanceKm - b.distanceKm)
      .slice(0, limit);

    return mapped.length ? mapped : localFallback();
  } catch {
    return localFallback();
  }
}
